<script setup lang="ts">
import type { ContentItem } from "@edco/apollo";
import type { LocationQueryValue } from "vue-router";
import type { KnowledgebaseSearch } from "~/models/generated/KnowledgebaseSearch";
import type { SearchResponseDto } from "#imports";
import { useSupportSearchResource } from "#imports";

defineProps<{
  content: ContentItem<KnowledgebaseSearch>;
}>();

const search = useSupportSearchResource();
const route = useRoute();

const pageSize = 12;

function getFirst ( val: LocationQueryValue | LocationQueryValue[] ) {
  return Array.isArray( val )
    ? val[0]?.toString()
    : val?.toString();
}

const pageIndex = ref( 0 );
const query = ref( getFirst( route.query.query ) );

function buildQuery () {
  const result: {
    page?: number;
    query?: string;
  } = {};

  if ( pageIndex.value !== 0 ) {
    result.page = pageIndex.value + 1;
  }

  if ( query.value && query.value !== "" ) {
    result.query = query.value;
  }

  return result;
}

const pending = ref( true );
const results = ref<SearchResponseDto>();

async function runSearch () {
  pending.value = true;
  results.value = await search.getResults( query.value, {
    pageIndex: pageIndex.value,
    pageSize,
  } );
  pending.value = false;
}

watch( query, async () => {
  pageIndex.value = 0;
  await runSearch();
  await navigateTo( {
    query: buildQuery(),
  } );
} );

watch( pageIndex, async () => {
  await runSearch();
  await navigateTo( {
    query: buildQuery(),
  } );
} );

watch( () => route.query, ( val ) => {
  if ( val.page !== undefined ) {
    pageIndex.value = Number.parseInt( getFirst( val.page ) ?? "", 10 ) - 1;
  } else {
    pageIndex.value = 0;
  }

  query.value = getFirst( val.query );
}, { deep: true, immediate: true } );

const pageCount = computed( () => Math.ceil( ( results.value?.total ?? 1 ) / pageSize ) );

const resultsHeading = ref<HTMLHeadingElement>();

const scrolling = ref( false );
const scrollDelay = 700;

function scrollToResults () {
  scrolling.value = true;
  resultsHeading.value?.scrollIntoView();
}

function submit ( val: string ) {
  query.value = val;
}

function onNext () {
  scrollToResults();
  setTimeout( () => {
    pageIndex.value += 1;
    scrolling.value = false;
  }, scrollDelay );
}

function onPrev () {
  scrollToResults();
  setTimeout( () => {
    pageIndex.value -= 1;
    scrolling.value = false;
  }, scrollDelay );
}

function onChangePage ( idx: number ) {
  scrollToResults();
  setTimeout( () => {
    pageIndex.value = idx;
    scrolling.value = false;
  }, scrollDelay );
}

const searchResultsLabel = computed( () => {
  if ( query.value && query.value.trim() !== "" ) {
    return `Search results for "${query.value}"`;
  }

  return null;
} );

await runSearch();
</script>

<template>
  <div class="container">
    <breadcrumb-menu :content="content" />

    <div class="knowledgebase-search-intro">
      <h1 class="knowledgebase-search-intro__heading">
        {{ content.properties.heading || content.name }}
      </h1>

      <quick-search
        :model-value="query"
        class="knowledgebase-search-intro__search"
        @search="submit" />
    </div>

    <section class="search-section">
      <div class="container">
        <h2
          v-if="searchResultsLabel"
          ref="resultsHeading"
          class="search-results__heading">
          {{ searchResultsLabel }}
        </h2>

        <div class="search-results">
          <ul
            v-if="results?.items && results.items.length"
            class="search-results__list">
            <li
              v-for="(result, index) in results?.items"
              :key="index"
              class="search-results__item">
              <SearchResult :content="result" />
            </li>
          </ul>
          <div v-else>
            No results found
          </div>

          <div
            v-if="pending || scrolling"
            class="search-results__loading">
            <a-spinner size="60px" />
          </div>
        </div>

        <ABasePagination
          v-if="pageCount > 1"
          :page-count="pageCount"
          :page-index="pageIndex"
          previous-prefix="fa:solid:chevron-left"
          :previous-suffix="null"
          :next-prefix="null"
          next-suffix="fa:solid:chevron-right"
          button-look="ghost"
          @next="onNext"
          @prev="onPrev"
          @change-page="onChangePage" />
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.container {
  width: 100%;
}

.knowledgebase-search-intro {
  background: #f8f8f8;
  padding: 60px;
  margin-bottom: 60px;
  margin-top: 15px;

  @media ( width <= 991px ) {
    padding: 20px;
  }

  &__heading {
    font-size: 3rem;
    margin-bottom: 20px;

    @media ( width <= 1199px ) {
      font-size: 2.5rem;
    }

    @media ( width <= 991px ) {
      font-size: 2rem;
    }
  }

  &__description {
    font-weight: 500;
    margin-bottom: 20px;
  }

  ::v-deep( input ) {
    width: 100%;
    padding: 20px;
    border-radius: 10px;
    border: 1px solid #ccc;
    height: auto !important;
  }

  .search-filter-group {
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }
  }
}

.search-section {
  padding: 60px 0;

  @media (width <= 767px) {
    padding-top: 0;
  }
}

.search-results {
  position: relative;

  &__heading {
    margin-bottom: 20px;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__loading {
    position: absolute;
    inset: -10px;
    background: #fff6;
    display: flex;
    justify-content: center;
    z-index: 3;
    padding: 60px 0;
    backdrop-filter: blur( 1px );

    .spinner {
      position: sticky;
      top: calc( 50% - 30px );
    }
  }
}
</style>
